import { LegacyChainClient } from '../core/LegacyChainClient';
import { PlanType } from '../types';

export interface BrandingConfig {
  companyName: string;
  logo: {
    light: string; // URL or base64
    dark: string;
    favicon?: string;
  };
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    text: string;
  };
  fonts: {
    heading: string;
    body: string;
  };
  customCSS?: string;
  emailTemplates?: {
    header?: string;
    footer?: string;
    styles?: string;
  };
  whiteLabel: {
    hideLegacyChainBranding: boolean;
    customDomain?: string;
    customEmailDomain?: string;
  };
}

export interface BrandedWidget {
  id: string;
  type: 'capsule_form' | 'payment_widget' | 'document_viewer' | 'analytics_dashboard';
  config: Record<string, any>;
  embedCode: string;
}

export class BrandingService {
  constructor(private client: LegacyChainClient) {}

  /**
   * Check if custom branding is available
   */
  private checkBrandingAccess() {
    const planType = this.client.getPlanType();
    if (planType !== PlanType.PRO && planType !== PlanType.ENTERPRISE) {
      throw new Error('Custom branding requires Pro or Enterprise plan');
    }
  }

  /**
   * Get current branding configuration
   */
  async getBranding(): Promise<BrandingConfig | null> {
    this.checkBrandingAccess();

    const response = await this.client.makeRequest<BrandingConfig>(
      'GET',
      '/branding'
    );

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to get branding');
    }

    return response.data || null;
  }

  /**
   * Update branding configuration
   */
  async updateBranding(config: Partial<BrandingConfig>): Promise<BrandingConfig> {
    this.checkBrandingAccess();

    const response = await this.client.makeRequest<BrandingConfig>(
      'PUT',
      '/branding',
      config
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to update branding');
    }

    return response.data;
  }

  /**
   * Upload logo
   */
  async uploadLogo(file: File, type: 'light' | 'dark' | 'favicon'): Promise<string> {
    this.checkBrandingAccess();

    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    const response = await this.client.makeRequest<{ url: string }>(
      'POST',
      '/branding/logo',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to upload logo');
    }

    return response.data.url;
  }

  /**
   * Create branded widget
   */
  async createWidget(input: {
    type: BrandedWidget['type'];
    config: Record<string, any>;
  }): Promise<BrandedWidget> {
    this.checkBrandingAccess();

    const response = await this.client.makeRequest<BrandedWidget>(
      'POST',
      '/branding/widgets',
      input
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to create widget');
    }

    return response.data;
  }

  /**
   * Get widget embed code
   */
  async getWidgetEmbedCode(widgetId: string): Promise<string> {
    this.checkBrandingAccess();

    const response = await this.client.makeRequest<{ embedCode: string }>(
      'GET',
      `/branding/widgets/${widgetId}/embed`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to get embed code');
    }

    return response.data.embedCode;
  }

  /**
   * Preview branding changes
   */
  async previewBranding(config: Partial<BrandingConfig>): Promise<{
    previewUrl: string;
    expiresAt: Date;
  }> {
    this.checkBrandingAccess();

    const response = await this.client.makeRequest<any>(
      'POST',
      '/branding/preview',
      config
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to generate preview');
    }

    return response.data;
  }

  /**
   * Set up custom domain (Enterprise only)
   */
  async setupCustomDomain(domain: string): Promise<{
    success: boolean;
    dnsRecords: Array<{
      type: 'A' | 'CNAME' | 'TXT';
      name: string;
      value: string;
    }>;
    verificationStatus: 'pending' | 'verified' | 'failed';
  }> {
    const planType = this.client.getPlanType();
    if (planType !== PlanType.ENTERPRISE) {
      throw new Error('Custom domains require Enterprise plan');
    }

    const response = await this.client.makeRequest<any>(
      'POST',
      '/branding/custom-domain',
      { domain }
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to setup custom domain');
    }

    return response.data;
  }

  /**
   * Verify custom domain
   */
  async verifyCustomDomain(domain: string): Promise<{
    verified: boolean;
    issues?: string[];
  }> {
    const planType = this.client.getPlanType();
    if (planType !== PlanType.ENTERPRISE) {
      throw new Error('Custom domains require Enterprise plan');
    }

    const response = await this.client.makeRequest<any>(
      'POST',
      `/branding/custom-domain/${domain}/verify`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to verify domain');
    }

    return response.data;
  }

  /**
   * Generate branded mobile app (Enterprise only)
   */
  async generateMobileApp(config: {
    platforms: Array<'ios' | 'android'>;
    appName: string;
    bundleId: string;
    version: string;
  }): Promise<{
    buildId: string;
    status: 'queued' | 'building' | 'completed' | 'failed';
    downloadUrls?: {
      ios?: string;
      android?: string;
    };
  }> {
    const planType = this.client.getPlanType();
    if (planType !== PlanType.ENTERPRISE) {
      throw new Error('Branded mobile apps require Enterprise plan');
    }

    const response = await this.client.makeRequest<any>(
      'POST',
      '/branding/mobile-app',
      config
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to generate mobile app');
    }

    return response.data;
  }

  /**
   * Get brand guidelines
   */
  async getBrandGuidelines(): Promise<{
    documentUrl: string;
    assets: Array<{
      name: string;
      type: string;
      url: string;
    }>;
  }> {
    this.checkBrandingAccess();

    const response = await this.client.makeRequest<any>(
      'GET',
      '/branding/guidelines'
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to get brand guidelines');
    }

    return response.data;
  }
} 